/**
 * @typedef {import("src/types").Restaurant} Restaurant
 * @typedef {import("src/types").UserGeolocation} UserLocation
 * @typedef {import("src/types").SelectedLocation} SelectedLocation
 * @typedef {import("src/types").LocationPickerOption} LocationPickerOption
 */
import { computed, Signal, signal } from "@preact/signals";
import settings from "src/services/settings.js";

/**
 * Bootstraps the global app state
 *
 * @param {SelectedLocation} location Sets the default location if cached
 * @returns ReturnType<createAppState>
 */
export function createAppState(location) {
  /** @type Signal<SelectedLocation> */
  const selectedLocation = signal(location);
  const selectedRid = signal("");
  /** @type Signal<number | string> */
  const selectedOrderId = signal(null);
  const activeRid = signal("");
  const isFetchingRestaurants = signal(false);
  /** @type Signal<Restaurant[]> A store of all the restaurants */
  const restaurants = signal([]);


  const locationRegionsByCountry = computed(() => {
    /** @type Map<string, Set<LocationPickerOption>> */
    const byCountry = new Map();
  
    restaurants.value.forEach((r) => {
      const country = r.acf.country;
  
      let locations = byCountry.get(country);
  
      if (!locations) {
        locations = new Set();
        byCountry.set(country, locations);
      }
  
      locations.add({
        label: r.title.rendered,
        id: r.id,
        rid: r.acf.opentable_rid,
      });
    });
  
    return {
      byCountry,
    };
  });
  const selectedLocationRid = computed(() => {
    if (DEV) {
      return selectedRid.value;
    }
    const target = restaurants.value.find((r) => {
      if (selectedRid.value) {
        return r.acf.opentable_rid === selectedRid.value;
      }
      return r.id === selectedLocation.value?.id;
    });
    return target?.acf.opentable_rid;
  });
  const selectedRestaurant = computed(() => {

    console.log("restaurants", restaurants.value);
  
    const availableRestaurants = (restaurants.value ?? []).filter((r, i) => {
  
      if (!r?.acf) {
        console.error("Bad restaurant", i, r);
        return false;
      }
  
      return !!r.acf.opentable_rid;
    });
  
    // if (DEV) {
    //   const first = availableRestaurants[0];
    //   if (first) {
    //     return {
    //       ...first,
    //       id: selectedRid.value,
    //       acf: { ...first.acf, opentable_rid: selectedRid.value },
    //     };
    //   }
    // }
  
    if (selectedRid.value) {
      return availableRestaurants.find(
        (r) => r.acf.opentable_rid === selectedRid.value
      );
    }
  
    const ridMatch = availableRestaurants.find(
      (r) => r.acf.opentable_rid === selectedLocationRid.value
    );
  
    if (!ridMatch) {
      return availableRestaurants.find(
        (r) => r.id === selectedLocation.value?.id
      );
    }
  
    return ridMatch;
  
  });
  // Query Form parts
  const currentTime = new Date();
  // Bump to the closest minute if not before the default hour (7pm)
  if (currentTime.getHours() > settings.defaultReservationHour) {
    const currentMinutes = currentTime.getMinutes();
    const quarterHourRemainder = currentMinutes % 15;
    if (quarterHourRemainder !== 0) {
      currentTime.setMinutes(currentMinutes + (15 - quarterHourRemainder));
    }
  } else {
    currentTime.setHours(settings.defaultReservationHour, 0);
  }
  currentTime.setSeconds(0);
  const partySize = signal(2);
  const preferredDate = signal(currentTime);
  const selectedTime = signal("");
  /** @type Signal<Date | null> */
  const selectedTimeDate = signal(null);
  const selectedSeating = signal("");
  const isLocationConfirmed = signal(false);
  const experienceIDs = signal([]);
  const isNextStepVisible = signal(false);

  const isSelectionValid = computed(() => {
    return selectedTime.value !== "" && selectedSeating.value !== "" && isNextStepVisible.value;
  });

  const lockedReservation = signal(null);
  /** @type Signal<Date | null> */
  const pendingTimer = signal(null);
  const reservationToken = signal("");
  const isExpired = signal(false);
  const liveAnnouncement = signal("");
  const lastAnnouncement = signal("");
  const timerTick = signal(0);

  /** @type Signal<import("src/modules/reservations/opentable").OTConfirmation> */
  const confirmedReservation = signal(null);

  return {
    isLocationConfirmed,
    isFetchingRestaurants,
    restaurants,
    locationRegionsByCountry,
    selectedLocation,
    selectedOrderId,
    /** Can be either the reserve button locked to the current selectedLocation, or from a separate location */
    selectedRid,
    activeRid,
    selectedLocationRid,
    /** Returns all the details of the active restaurant from the DB */
    selectedRestaurant,

    // QueryForm values
    partySize,
    preferredDate,
    /** The server API returned value, stored for convienence */
    selectedTime,
    /** Date representation for easier parsing */
    selectedTimeDate,
    selectedSeating,
    experienceIDs,
    isSelectionValid,
    isNextStepVisible,
    // QueryFormData
    lockedReservation,
    pendingTimer,
    timerTick,
    reservationToken,
    isExpired,
    liveAnnouncement,
    lastAnnouncement,

    confirmedReservation,
  };
}

/**
 * Check cookies for the current location
 * @returns {SelectedLocation | null}
 */
export function checkCache() {
  try {
    const cached = getCookie(settings.locationKey);
    /** @type SelectedLocation */
    const restaurant = JSON.parse(cached);

    return restaurant;
  } catch (err) {
    console.error("Cache Error:", err);
    return null;
  }
}

/**
 * @param {SelectedLocation} restaurant
 */
export function cacheLocation(restaurant) {
  const event = new CustomEvent("selected-restaurant-updated", {
    bubbles: true,
    detail: restaurant,
  });
  const value = JSON.stringify(restaurant);
  setCookie(settings.locationKey, value);
  setCookie("moxies_location_name_clean", restaurant.title);
  document.body.dispatchEvent(event);
}
/**
 * @param {string} key
 * @param {string} value
 */
export function setCookie(key, value) {
  let date = new Date();
  date.setTime(
    date.getTime() + settings.locationExpiryDays * 24 * 60 * 60 * 1000
  );
  const expires = date.toUTCString();
  const encodedValue = encodeURIComponent(value);
  document.cookie = `${key}=${encodedValue}; expires=${expires}; path=/; secure; SameSite=Strict`;
}

/**
 * @param {string} key
 * @returns {string|null}
 */
export function getCookie(key) {
  const cookies = document.cookie.split(";");

  const value = cookies.reduce((memo, cookie) => {
    const [cookieKey, cookieValue] = cookie.trim().split("=");
    if (cookieKey === key) {
      const decoded = decodeURIComponent(cookieValue);
      return decoded;
    }
    return memo;
  }, null);

  return value;
}
